import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, FileText, Video, Download, Star, ArrowRight, Plus, Check, Play, Clock, BookOpen, Lock } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StorePage = () => {
  const [products, setProducts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [purchasedCourseIds, setPurchasedCourseIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { addItem, items } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Demo products - these will come from backend later
  const demoProducts = [
    {
      id: 'prod-1',
      title: 'Emocijų dienoraštis',
      description: 'PDF darbo knyga su pratimais emocijų atpažinimui ir valdymui. 30 dienų programa.',
      price: 9.99,
      category: 'pdf',
      file_type: 'PDF',
      preview_image: null,
      features: ['30 dienų programa', '50+ pratimų', 'Printinti galima']
    },
    {
      id: 'prod-2',
      title: 'Streso valdymo gidas',
      description: 'Išsamus vadovas su praktinėmis technikomis kasdieniam streso valdymui.',
      price: 14.99,
      category: 'pdf',
      file_type: 'PDF',
      preview_image: null,
      features: ['80 puslapių', 'Audio meditacijos', 'Praktinės užduotys']
    },
    {
      id: 'prod-4',
      title: 'Konfliktų sprendimo strategijos',
      description: 'PDF vadovas su konkrečiomis strategijomis konfliktų valdymui.',
      price: 12.99,
      category: 'pdf',
      file_type: 'PDF',
      preview_image: null,
      features: ['45 puslapių', 'Situacijų pavyzdžiai', 'Šablonai']
    },
    {
      id: 'prod-5',
      title: 'Sąmoningumo praktika',
      description: 'Audio meditacijų rinkinys kasdienei sąmoningumo praktikai.',
      price: 19.99,
      category: 'audio',
      file_type: 'Audio',
      preview_image: null,
      features: ['20 meditacijų', '3+ valandos', 'MP3 formatas']
    },
    {
      id: 'prod-6',
      title: 'Viešojo kalbėjimo vadovas',
      description: 'Viskas, ko reikia norint tapti užtikrintu kalbėtoju.',
      price: 24.99,
      category: 'pdf',
      file_type: 'PDF + Video',
      preview_image: null,
      features: ['PDF vadovas', '2 val. video', 'Pratimų rinkinys']
    }
  ];

  // Demo video courses
  const demoCourses = [
    {
      id: 'course-1',
      title: 'Emocinio intelekto pagrindai',
      description: 'Išsamus video kursas apie emocinio intelekto ugdymą. Išmoksite atpažinti, suprasti ir valdyti savo emocijas.',
      price: 49.99,
      category: 'video',
      duration: '5 valandos',
      lessons_count: 10,
      color: 'bg-[#4CAF50]',
      lightColor: 'bg-[#E8F5E9]',
      features: ['10 video pamokų', '5 val. turinio', 'Prieiga 2 metus']
    },
    {
      id: 'course-2',
      title: 'Streso valdymo meistras',
      description: 'Praktinis kursas su konkrečiomis technikomis kasdieniam streso valdymui darbe ir asmeniniame gyvenime.',
      price: 39.99,
      category: 'video',
      duration: '3 valandos',
      lessons_count: 8,
      color: 'bg-[#FF9F43]',
      lightColor: 'bg-[#FFF3E0]',
      features: ['8 video pamokos', '3 val. turinio', 'Prieiga 2 metus']
    },
    {
      id: 'course-3',
      title: 'Sąmoningas bendravimas',
      description: 'Kursas apie efektyvų ir sąmoningą bendravimą. Išmoksite klausyti, kalbėti ir spręsti konfliktus.',
      price: 59.99,
      category: 'video',
      duration: '4 valandos',
      lessons_count: 12,
      color: 'bg-[#5B9BD5]',
      lightColor: 'bg-[#E3F2FD]',
      features: ['12 video pamokų', '4 val. turinio', 'Prieiga 2 metus']
    }
  ];

  useEffect(() => {
    loadData();
  }, [user, token]);

  const loadData = async () => {
    try {
      // Load products
      try {
        const response = await fetch(`${API_URL}/api/products`);
        const data = await response.json();
        if (data && data.length > 0) {
          setProducts(data);
        } else {
          setProducts(demoProducts);
        }
      } catch {
        setProducts(demoProducts);
      }

      // Load courses
      try {
        const response = await axios.get(`${API_URL}/api/courses`);
        if (response.data && response.data.length > 0) {
          setCourses(response.data);
        } else {
          setCourses(demoCourses);
        }
      } catch {
        setCourses(demoCourses);
      }

      // Load purchased courses if logged in
      if (user && token) {
        try {
          const response = await axios.get(`${API_URL}/api/my-courses`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setPurchasedCourseIds(response.data.map(c => c.course_id));
        } catch {
          // Ignore
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'Visi produktai', icon: ShoppingBag },
    { id: 'pdf', label: 'PDF', icon: FileText },
    { id: 'video', label: 'Video kursai', icon: Video },
    { id: 'audio', label: 'Audio', icon: Download }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : selectedCategory === 'video' 
    ? [] // Video courses are shown separately
    : products.filter(p => p.category === selectedCategory);

  const filteredCourses = selectedCategory === 'all' || selectedCategory === 'video'
    ? courses
    : [];

  const handleAddToCart = (product) => {
    addItem({
      id: product.id,
      title: product.title,
      price: product.price,
      quantity: 1,
      type: 'product'
    });
    toast.success(`"${product.title}" pridėta į krepšelį!`);
  };

  const handleAddCourseToCart = (course) => {
    addItem({
      id: course.id,
      title: course.title,
      price: course.price,
      quantity: 1,
      type: 'course'
    });
    toast.success(`"${course.title}" pridėta į krepšelį!`);
  };

  const isInCart = (productId) => {
    return items.some(item => item.id === productId);
  };

  const isCourseInCart = (courseId) => {
    return items.some(item => item.id === courseId);
  };

  const isCoursePurchased = (courseId) => {
    return purchasedCourseIds.includes(courseId);
  };

  const getProductIcon = (category) => {
    switch(category) {
      case 'pdf': return FileText;
      case 'video': return Video;
      case 'audio': return Download;
      default: return FileText;
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'pdf': return { bg: 'bg-[#E8F5E9]', text: 'text-[#4CAF50]', solid: 'bg-[#4CAF50]' };
      case 'video': return { bg: 'bg-[#E3F2FD]', text: 'text-[#5B9BD5]', solid: 'bg-[#5B9BD5]' };
      case 'audio': return { bg: 'bg-[#F3E5F5]', text: 'text-[#9B59B6]', solid: 'bg-[#9B59B6]' };
      default: return { bg: 'bg-[#FFF3E0]', text: 'text-[#FF9F43]', solid: 'bg-[#FF9F43]' };
    }
  };

  return (
    <div data-testid="store-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#FAFAF8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-[#E8F5E9] text-[#4CAF50] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <ShoppingBag className="w-4 h-4" />
              <span>Skaitmeniniai produktai</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-['Poppins'] font-bold text-[#2D3748] mb-4">
              Parduotuvė
            </h1>
            <p className="text-lg text-[#718096]">
              PDF vadovai, video kursai ir audio meditacijos jūsų asmeniniam augimui
            </p>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => {
              const Icon = category.icon;
              const isActive = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-5 py-2.5 rounded-full font-medium transition-all ${
                    isActive 
                      ? 'bg-[#4CAF50] text-white' 
                      : 'bg-[#FAFAF8] text-[#718096] hover:bg-[#E8F5E9] hover:text-[#4CAF50]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{category.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[#718096]">Kraunami produktai...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
              <p className="text-[#718096]">Produktų nerasta</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProducts.map((product) => {
                const Icon = getProductIcon(product.category);
                const colors = getCategoryColor(product.category);
                const inCart = isInCart(product.id);
                
                return (
                  <div
                    key={product.id}
                    data-testid={`product-card-${product.id}`}
                    className="bg-white rounded-3xl border-2 border-slate-100 overflow-hidden hover:border-transparent hover:shadow-xl transition-all group"
                  >
                    {/* Product Header */}
                    <div className={`${colors.solid} p-6 relative`}>
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <span className="absolute top-4 right-4 bg-white/20 text-white text-xs px-3 py-1 rounded-full font-medium">
                        {product.file_type}
                      </span>
                    </div>

                    {/* Product Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-['Poppins'] font-semibold text-[#2D3748] mb-2">
                        {product.title}
                      </h3>
                      <p className="text-[#718096] text-sm mb-4 leading-relaxed">
                        {product.description}
                      </p>

                      {/* Features */}
                      {product.features && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {product.features.map((feature, idx) => (
                            <span key={idx} className={`${colors.bg} ${colors.text} text-xs px-2 py-1 rounded-full`}>
                              {feature}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Price and CTA */}
                      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <div>
                          <span className="text-2xl font-bold text-[#2D3748]">{product.price.toFixed(2)}</span>
                          <span className="text-[#718096] ml-1">€</span>
                        </div>
                        <button
                          onClick={() => handleAddToCart(product)}
                          disabled={inCart}
                          className={`flex items-center space-x-2 px-5 py-2.5 rounded-full font-medium transition-all ${
                            inCart
                              ? 'bg-[#E8F5E9] text-[#4CAF50]'
                              : `${colors.solid} text-white hover:opacity-90`
                          }`}
                        >
                          {inCart ? (
                            <>
                              <Check className="w-4 h-4" />
                              <span>Krepšelyje</span>
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4" />
                              <span>Į krepšelį</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#4CAF50] py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl font-['Poppins'] font-bold text-white mb-4">
            Ieškote individualizuotų mokymų?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Susisiekite ir sukursime programą pagal jūsų poreikius
          </p>
          <Link
            to="/kontaktai"
            className="inline-flex items-center bg-white text-[#4CAF50] hover:bg-slate-100 rounded-full px-8 py-4 font-semibold transition-colors"
          >
            Susisiekti
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      {/* Legal Info Section */}
      <section className="bg-[#FAFAF8] py-12 border-t border-slate-200">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-8">
            <h3 className="text-lg font-['Poppins'] font-semibold text-[#2D3748] mb-2">
              Svarbi informacija pirkėjams
            </h3>
            <p className="text-[#718096] text-sm">
              Prieš pirkdami, susipažinkite su mūsų taisyklėmis ir sąlygomis
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Link to="/salygos" className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow border border-slate-100">
              <FileText className="w-6 h-6 text-[#4CAF50] mx-auto mb-2" />
              <span className="text-sm font-medium text-[#2D3748]">Pirkimo taisyklės</span>
            </Link>
            <Link to="/pristatymas" className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow border border-slate-100">
              <Download className="w-6 h-6 text-[#5B9BD5] mx-auto mb-2" />
              <span className="text-sm font-medium text-[#2D3748]">Pristatymo sąlygos</span>
            </Link>
            <Link to="/grazinimas" className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow border border-slate-100">
              <ArrowRight className="w-6 h-6 text-[#9B59B6] mx-auto mb-2 rotate-180" />
              <span className="text-sm font-medium text-[#2D3748]">Grąžinimo politika</span>
            </Link>
            <Link to="/privatumas" className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow border border-slate-100">
              <Star className="w-6 h-6 text-[#FF9F43] mx-auto mb-2" />
              <span className="text-sm font-medium text-[#2D3748]">Privatumo politika</span>
            </Link>
          </div>
          <div className="bg-white rounded-xl p-6 border border-slate-100">
            <h4 className="font-semibold text-[#2D3748] mb-3">Pardavėjo informacija</h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-[#718096]">
              <div>
                <p><strong>Pardavėjas:</strong> Dmitrij Borodin</p>
                <p><strong>Ind. veiklos pažymos nr.:</strong> 706574</p>
              </div>
              <div>
                <p><strong>El. paštas:</strong> info@tavoemocijos.lt</p>
                <p><strong>Telefonas:</strong> +370 678 38321</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StorePage;
